import React from 'react'
import PropTypes from 'prop-types'
import { BqButton } from '@beeq/react'
import { Formik } from 'formik'
import { Text } from '../../../constants/TextConstants'
import UsersAdvancedFiltersSelect from '../UsersAdvancedFiltersSelect'
import UsersAdvancedFiltersSelectedFilters from '../UsersAdvancedFiltersSelectedFilters'
import NestedSelect from '../NestedSelect'
import {
  StyledBqDrawer,
  StyledBqDrawerContent,
  StyledBqDrawerSection,
  StyledFooter,
  StyledBqDivider,
  StyledFieldsWrapper
} from './styles'

/**
 * @typedef {Object} LocationPermission
 * @property {string} name
 * @property {Object[]} countries
 * @property {string} countries.name
 * @property {string[]} countries.DU
 */

/**
 * @typedef {Object} InitialValues
 * @property {string[]} DU
 * @property {string[]} grade
 * @property {string[]} discipline
 * @property {string[]} userRole
 * @property {string[]} disciplinePermission
 * @property {unknown[]} locationPermission
 */

/**
 * @typedef {Object} Options
 * @property {string[]} deliveryUnits
 * @property {string[]} grades
 * @property {string[]} disciplines
 * @property {string[]} userRoles
 * @property {string[]} disciplinePermissions
 * @property {LocationPermission[]} locationPermissions
 */

/**
 * @typedef {Object} UsersAdvancedFiltersProps
 * @property {boolean} open
 * @property {Options} options
 * @property {InitialValues} initialValues
 * @property {() => void} onClose
 * @property {InitialValues} resetValues
 */

/**
 * @param {UsersAdvancedFiltersProps} props
 * @returns {import('react').ReactElement}
 */
export default function UsersAdvancedFilters({ open, onClose, options, initialValues, onFilter, resetValues }) {
  return (
        <StyledBqDrawer
            placement='right'
            open={open}
            onBqClose={onClose}
            enableBackdrop
            closeOnClickOutside
            closeOnEsc
        >
            {open && <Formik
                onSubmit={onFilter}
                initialValues={initialValues}

            >
                {({ submitForm, resetForm, values, handleChange, handleBlur, setFieldValue }) => (
                    <>
                        <div slot='title'>
                            {Text.UsersAdvancedFiltersTitle}
                        </div>
                        <StyledBqDrawerContent>
                            <StyledBqDrawerSection>
                                <UsersAdvancedFiltersSelectedFilters values={values} onClose={setFieldValue} />
                            </StyledBqDrawerSection>
                            <StyledFieldsWrapper>
                            <StyledBqDrawerSection>
                                <UsersAdvancedFiltersSelect
                                    name='DU'
                                    value={values.DU}
                                    options={options.deliveryUnits}
                                    label={Text.UsersAdvancedFiltersUserDeliveryLocation}
                                    onSelect={handleChange}
                                    onBlur={handleBlur}
                                />
                                <UsersAdvancedFiltersSelect
                                    name='grade'
                                    value={values.grade}
                                    options={options.grades}
                                    label={Text.UsersAdvancedFiltersUserGrade}
                                    onSelect={handleChange}
                                    onBlur={handleBlur}
                                />
                                <UsersAdvancedFiltersSelect
                                    name='discipline'
                                    value={values.discipline}
                                    options={options.disciplines}
                                    label={Text.UsersAdvancedFiltersUserDiscipline}
                                    onSelect={handleChange}
                                    onBlur={handleBlur}
                                />
                            </StyledBqDrawerSection>
                            <StyledBqDrawerSection>
                                <div className='h6 bold'>{Text.UsersAdvancedFiltersAppliedPermissions}</div>
                                <UsersAdvancedFiltersSelect
                                    name='userRole'
                                    value={values.userRole}
                                    options={options.userRoles}
                                    label={Text.UsersAdvancedFiltersUserRole}
                                    onSelect={handleChange}
                                    onBlur={handleBlur}
                                />
                                <NestedSelect
                                    name='disciplinePermission'
                                    value={values.disciplinePermission}
                                    options={options.disciplinePermissions}
                                    label={Text.UsersAdvancedFiltersUserDisciplinePermission}
                                    selectedLabel={`(${values.disciplinePermission.length}) ${Text.UsersAdvancedFiltersUserLabelSelected}`}
                                    placeholder={Text.UsersAdvancedFiltersUserSelectPlaceholder}
                                    onSelect={(value) => setFieldValue('disciplinePermission', value)}
                                    onBlur={handleBlur}
                                />
                                <NestedSelect
                                    name='locationPermission'
                                    value={values.locationPermission}
                                    options={options.locationPermissions}
                                    label={Text.UsersAdvancedFiltersUserLocationPermission}
                                    selectedLabel={`(${values.locationPermission.length}) ${Text.UsersAdvancedFiltersUserLabelSelected}`}
                                    placeholder={Text.UsersAdvancedFiltersUserSelectPlaceholder}
                                    onSelect={(value) => setFieldValue('locationPermission', value)}
                                    onBlur={handleBlur}
                                />
                            </StyledBqDrawerSection>
                            </StyledFieldsWrapper>
                        </StyledBqDrawerContent>
                        <StyledBqDivider slot='footer-divider' strokeColor='ui--secondary' strokeThickness={1} />
                        <StyledFooter slot='footer'>
                            <BqButton
                                variant='ghost'
                                appearance='secondary'
                                block
                                size='large'
                                type='button'
                                data-testid="advanced-search-filter-clear-button"
                                onBqClick={() => {
                                  resetForm({ values: resetValues })
                                  submitForm()
                                }}
                            >
                                {Text.UsersAdvancedFiltersClear}
                            </BqButton>
                            <BqButton
                                variant='standard'
                                appearance='primary'
                                block
                                size='large'
                                type='button'
                                data-testid="advanced-search-filter-apply-button"
                                onBqClick={submitForm}
                            >
                                {Text.UsersAdvancedFiltersApply}
                            </BqButton>
                        </StyledFooter>
                    </>
                )}
            </Formik>}
        </StyledBqDrawer>
  )
}

const permissionShape = {
  value: PropTypes.string.isRequired
}

permissionShape.innerValues = PropTypes.arrayOf(PropTypes.shape(permissionShape))

UsersAdvancedFilters.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onFilter: PropTypes.func.isRequired,
  resetValues: PropTypes.shape({
    DU: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    grade: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    discipline: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    userRole: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    disciplinePermission: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired
  }),
  options: PropTypes.shape({
    deliveryUnits: PropTypes.arrayOf(PropTypes.string.isRequired),
    grades: PropTypes.arrayOf(PropTypes.string.isRequired),
    disciplines: PropTypes.arrayOf(PropTypes.string.isRequired),
    userRoles: PropTypes.arrayOf(PropTypes.string.isRequired),
    disciplinePermissions: PropTypes.arrayOf(PropTypes.shape(permissionShape).isRequired),
    locationPermissions: PropTypes.arrayOf(PropTypes.shape(permissionShape).isRequired)
  }),
  initialValues: PropTypes.shape({
    DU: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    grade: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    discipline: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    userRole: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    disciplinePermission: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired
  })
}
